import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { ColumnDef } from "@tanstack/react-table";

import type { DriverRow } from "@/types/driver";
import ColumnHeader from "@/ui/ColumnHeader";
import type { DataTableFeatures } from "@/ui/dataTableFeatures";
import { zinc } from "@/ui/tokens";
import { sanitizeHtmlForDisplay } from "@/utils/sanitizeHtmlForDisplay";
import { columnWidthFloors } from "./columnMinWidth";

const RECENT_MESSAGE_COLUMN_TITLE = "Most Recent Driver Message";

/** Hover dwell before the full-message tooltip appears. */
export const RECENT_MESSAGE_TOOLTIP_ENTER_DELAY_MS = 2500;
/**
 * Grace period before the tooltip hides after the cursor leaves. Re-entering the
 * cell within this window cancels the hide (MUI clears the leave timer on re-enter).
 */
export const RECENT_MESSAGE_TOOLTIP_LEAVE_DELAY_MS = 1000;

/**
 * Renders the truncated recent message, revealing the full text in a tooltip on hover.
 *
 * The tooltip follows the cursor and opens up-and-to-the-right (`right-end`) so it never
 * covers the rows below, letting the user move down message-to-message reading each in full.
 */
export function RecentMessageCell({ value }: { value: string | null }) {
  const html = sanitizeHtmlForDisplay(value ?? "");

  const cell = (
    <Box
      className="display-transcript"
      component="div"
      dangerouslySetInnerHTML={{ __html: html }}
      sx={{
        color: zinc[400],
        display: "-webkit-box",
        fontSize: "0.875rem",
        maxHeight: "2.75rem",
        overflow: "hidden",
        wordBreak: "break-word",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,
      }}
    />
  );

  if (!value) return cell;

  return (
    <Tooltip
      title={
        <Box
          className="display-transcript"
          component="div"
          dangerouslySetInnerHTML={{ __html: html }}
          sx={{ fontSize: "0.875rem", wordBreak: "break-word", whiteSpace: "pre-wrap" }}
        />
      }
      followCursor
      disableInteractive
      placement="right-end"
      enterDelay={RECENT_MESSAGE_TOOLTIP_ENTER_DELAY_MS}
      enterNextDelay={RECENT_MESSAGE_TOOLTIP_ENTER_DELAY_MS}
      leaveDelay={RECENT_MESSAGE_TOOLTIP_LEAVE_DELAY_MS}
      slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
    >
      {cell}
    </Tooltip>
  );
}

export function recentMessageColumn(): ColumnDef<DataTableFeatures, DriverRow> {
  const { minSize, initialMinWidth } = columnWidthFloors(RECENT_MESSAGE_COLUMN_TITLE, {
    draggable: true,
    hasFilter: false,
  });
  return {
    accessorKey: "recentMessage",
    header: () => <ColumnHeader title={RECENT_MESSAGE_COLUMN_TITLE} />,
    cell: ({ getValue }) => <RecentMessageCell value={getValue<string | null>()} />,
    meta: { flex: 2, draggable: true, initialMinWidth },
    minSize,
    enableSorting: false,
    enableGlobalFilter: false,
  };
}
